import React from 'react';
import type {
  ProjectStatus, ProjectPriority, ExpenseCategory, PaymentMethod,
  ProjectCollaboratorRole,
} from '../../../api/types';

// ─── Label maps ───────────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  BOZZA: 'Bozza', PREVENTIVATO: 'Preventivato', CONFERMATO: 'Confermato',
  IN_CORSO: 'In corso', IN_ATTESA: 'In attesa', COMPLETATO: 'Completato',
  FATTURATO: 'Fatturato', CHIUSO: 'Chiuso', ANNULLATO: 'Annullato',
};
export const STATUS_COLORS: Record<ProjectStatus, string> = {
  BOZZA: 'var(--color-text-faint)', PREVENTIVATO: 'var(--color-text-muted)',
  CONFERMATO: 'var(--color-success)', IN_CORSO: 'var(--color-primary)',
  IN_ATTESA: 'var(--color-warning)', COMPLETATO: 'var(--color-gold)',
  FATTURATO: 'var(--color-success)', CHIUSO: 'var(--color-text-faint)', ANNULLATO: 'var(--color-error)',
};
export const PRIORITY_LABELS: Record<ProjectPriority, string> = { BASSA: 'Bassa', MEDIA: 'Media', ALTA: 'Alta', URGENTE: 'Urgente' };
export const PRIORITY_COLORS: Record<ProjectPriority, string> = {
  URGENTE: 'var(--color-error)', ALTA: 'var(--color-warning)',
  MEDIA: 'var(--color-primary)', BASSA: 'var(--color-text-faint)',
};
export const TYPE_LABELS: Record<string, string> = {
  INTERNO: 'Interno', EVENTO_CORRELATO: 'Evento correlato',
  POST_PRODUZIONE_ESTERNA: 'Post-produzione esterna', ALTRO: 'Altro',
};
export const EXT_JOB_LABELS: Record<string, string> = {
  SELEZIONE: 'Selezione', CULLING: 'Culling', EDITING_BASE: 'Editing base',
  EDITING_AVANZATO: 'Editing avanzato', IMPAGINAZIONE_ALBUM: 'Impaginazione album',
  EXPORT_GALLERIA: 'Export galleria', WORKFLOW_COMPLETO: 'Workflow completo', ALTRO: 'Altro',
};
export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'TRASPORTI', 'ALLOGGIO', 'PASTI', 'ATTREZZATURA', 'NOLEGGI',
  'COLLABORATORI', 'POST_PRODUZIONE', 'STAMPE', 'ALBUM', 'SOFTWARE', 'MARKETING', 'VARIE',
];
export const EXPENSE_CAT_LABELS: Record<ExpenseCategory, string> = {
  TRASPORTI: 'Trasporti', ALLOGGIO: 'Alloggio', PASTI: 'Pasti', ATTREZZATURA: 'Attrezzatura',
  NOLEGGI: 'Noleggi', COLLABORATORI: 'Collaboratori', POST_PRODUZIONE: 'Post-produzione',
  STAMPE: 'Stampe', ALBUM: 'Album', SOFTWARE: 'Software', MARKETING: 'Marketing', VARIE: 'Varie',
};
export const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CONTANTI', label: 'Contanti' }, { value: 'BONIFICO', label: 'Bonifico' },
  { value: 'CARTA', label: 'Carta' }, { value: 'PAYPAL', label: 'PayPal' }, { value: 'ALTRO', label: 'Altro' },
];
export const COLLAB_ROLE_LABELS: Record<ProjectCollaboratorRole, string> = {
  EDITOR: 'Editor', CULLER: 'Culler', ALBUM_DESIGNER: 'Album designer',
  REVIEWER: 'Reviewer', PROJECT_SUPPORT: 'Supporto progetto',
};
export const NEXT_STATUSES: Record<ProjectStatus, ProjectStatus[]> = {
  BOZZA: ['PREVENTIVATO', 'ANNULLATO'],
  PREVENTIVATO: ['CONFERMATO', 'ANNULLATO'],
  CONFERMATO: ['IN_CORSO', 'IN_ATTESA', 'ANNULLATO'],
  IN_CORSO: ['COMPLETATO', 'IN_ATTESA'],
  IN_ATTESA: ['IN_CORSO', 'COMPLETATO'],
  COMPLETATO: ['FATTURATO', 'CHIUSO'],
  FATTURATO: ['CHIUSO'],
  CHIUSO: [],
  ANNULLATO: [],
};

// ─── Shared sub-components ───────────────────────────────────────────────────

export function SectionCard({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export function InfoRow({ label, value }: { label: string; value?: string | null | React.ReactNode }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{value}</span>
    </div>
  );
}

export function KpiMini({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.875rem', flex: 1, minWidth: 120 }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>{label}</p>
      <p style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: color ?? 'var(--color-text)' }}>{value}</p>
      {sub && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.125rem' }}>{sub}</p>}
    </div>
  );
}

// ─── Mini badge helpers ───────────────────────────────────────────────────────

export function ExpenseStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = { DA_PAGARE: 'var(--color-warning)', PARZIALE: 'var(--color-primary)', PAGATO: 'var(--color-success)', ANNULLATO: 'var(--color-text-faint)' };
  const labels: Record<string, string> = { DA_PAGARE: 'Da pagare', PARZIALE: 'Parziale', PAGATO: 'Pagato', ANNULLATO: 'Annullato' };
  const c = colors[status] ?? 'var(--color-text-muted)';
  return <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: c }}>{labels[status] ?? status}</span>;
}

export function RevenueStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = { DA_INCASSARE: 'var(--color-warning)', PARZIALE: 'var(--color-primary)', INCASSATO: 'var(--color-success)', ANNULLATO: 'var(--color-text-faint)' };
  const labels: Record<string, string> = { DA_INCASSARE: 'Da incassare', PARZIALE: 'Parziale', INCASSATO: 'Incassato', ANNULLATO: 'Annullato' };
  const c = colors[status] ?? 'var(--color-text-muted)';
  return <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: c }}>{labels[status] ?? status}</span>;
}

export function PaymentStatusBadgeSmall({ status }: { status: string }) {
  const bgMap: Record<string, string> = { DA_PAGARE: '#F59E0B', PARZIALE: '#3E91FF', PAGATO: '#22C55E' };
  const labels: Record<string, string> = { DA_PAGARE: 'Da pagare', PARZIALE: 'Parziale', PAGATO: 'Pagato' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF', background: bgMap[status] ?? '#6B7280', padding: '4px 10px', borderRadius: 'var(--radius-interactive)', display: 'inline-flex', alignItems: 'center' }}>{labels[status] ?? status}</span>;
}
